/**
 * WorkspaceSection — 사이드바의 워크스페이스 목록 섹션.
 *
 * 조회·정렬·추가·이름 변경·삭제를 담당한다. 선택 상태는 소유하지 않고
 * (selection/onSelect로 페이지가 소유 — URL ?ws= 파라미터), CRUD는
 * workspaceStore를 직접 사용한다. 사이드바 축소 상태에서는 렌더되지 않는다
 * (Sidebar children 계약).
 */

import React, { useMemo, useState } from 'react';
import { ConfirmDestructiveDialog } from '@/shared/ui/ConfirmDestructiveDialog';
import { Box, Button, DialogActions, DialogContent, DialogTitle, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import { Dialog, Menu } from '@/shared/ui/overlays';
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  DriveFileRenameOutline as RenameIcon,
  Inbox as DefaultIcon,
  MoreVert as MoreIcon,
  SortByAlpha as SortNameIcon,
  Schedule as SortCreatedIcon,
  Workspaces as WorkspaceIcon,
} from '@mui/icons-material';
import { useWorkspaceStore } from '@/app/stores/workspaceStore';
import { WORKSPACE_NAME_MAX, storedName } from '@/domain/namePolicy';
import type { Workspace } from '@/types/project';
import { defaultWorkspaceOf, orderWorkspaces, type WorkspaceSort } from '@/app/workspaceFilter';

interface WorkspaceSectionProps {
  /** 현재 워크스페이스 id — 선택이 아직 보류(목록 적재 전)면 null */
  selection: string | null;
  onSelect: (selection: string) => void;
  /** 워크스페이스별 프로젝트 수 — 키는 소속 id */
  counts: Record<string, number>;
}

type SortMode = WorkspaceSort;

/** 편집 다이얼로그 상태 — 생성과 이름 변경이 폼을 공유한다 */
interface EditState {
  mode: 'create' | 'rename';
  workspace?: Workspace;
  name: string;
  description: string;
}

const WorkspaceSection: React.FC<WorkspaceSectionProps> = ({
  selection,
  onSelect,
  counts,
}) => {
  const { workspaces, error, createWorkspace, renameWorkspace, deleteWorkspace, clearError } =
    useWorkspaceStore();

  const [sortMode, setSortMode] = useState<SortMode>('created');
  const [edit, setEdit] = useState<EditState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Workspace | null>(null);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; workspace: Workspace } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // 기본 행(is_default)이 먼저, 나머지는 서버 정렬(created_at 오름차순) 또는 이름순 — 순서·판별은 app/workspaceFilter 하나
  const sorted = useMemo(() => orderWorkspaces(workspaces, sortMode), [workspaces, sortMode]);
  const defaultWorkspace = useMemo(() => defaultWorkspaceOf(workspaces), [workspaces]);

  const openCreate = () => {
    clearError();
    setEdit({ mode: 'create', name: '', description: '' });
  };

  const openRename = (workspace: Workspace) => {
    clearError();
    setMenu(null);
    setEdit({
      mode: 'rename',
      workspace,
      name: workspace.name,
      description: workspace.description,
    });
  };

  const handleEditSubmit = async () => {
    if (!edit || !edit.name.trim()) return;
    setSubmitting(true);
    try {
      if (edit.mode === 'create') {
        const created = await createWorkspace(storedName(edit.name), edit.description.trim());
        if (created.ok) {
          setEdit(null);
          onSelect(created.value.id); // 만든 곳으로 바로 이동 — 다음 동작이 "여기에 프로젝트 만들기"
        }
      } else if (edit.workspace) {
        const renamed = await renameWorkspace(
          edit.workspace.id,
          storedName(edit.name),
          edit.description.trim(),
        );
        if (renamed.ok) setEdit(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      const deleted = await deleteWorkspace(deleteTarget.id);
      if (deleted.ok) {
        // 삭제된 워크스페이스를 보고 있었다면 기본 행으로 복귀(서버가 그 프로젝트들을 거기로 옮겼다)
        if (selection === deleteTarget.id && defaultWorkspace) onSelect(defaultWorkspace.id);
        setDeleteTarget(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderRow = (workspace: Workspace) => {
    const key = workspace.id;
    const label = workspace.name;
    const count = counts[workspace.id];
    // 기본 행은 Inbox 아이콘(판별은 is_default), 나머지는 Workspaces 아이콘
    const icon = workspace.is_default ? <DefaultIcon fontSize="small" /> : <WorkspaceIcon fontSize="small" />;
    return (
    <ListItem
      key={key}
      disablePadding
      // 키보드 탐색(:focus-within)에서도 행 메뉴가 보여야 한다 — hover 전용이면
      // 포커스 이동으로는 메뉴 버튼이 나타나지 않는다
      // 개수는 ⋮ 메뉴와 같은 자리를 쓴다 — hover·focus 에 메뉴가 뜨고 개수는 opacity 로만 감춰 접근성 트리(행의 이름)에 남는다.
      sx={{
        '&:hover .ws-row-menu, &:focus-within .ws-row-menu': { opacity: 1 },
        '&:hover .ws-row-count, &:focus-within .ws-row-count': { opacity: 0 },
      }}
    >
      {/* 말줄임돼도 전체 명칭을 확인할 수 있게 툴팁 상시 제공 (#282). 행 전체에 앵커해
          사이드바 바깥(오른쪽)에 뜨게 한다 — 이름 칸에 앵커하면 꼬리 칸의 ⋮ 를 가린다. */}
      <Tooltip title={label} placement="right" enterDelay={500}>
        <ListItemButton
          selected={selection === key}
          onClick={() => onSelect(key)}
          sx={{ minHeight: 36, pl: 2, pr: 0.5, borderRadius: 1, mx: 0.5 }}
        >
          <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>{icon}</ListItemIcon>
          <ListItemText
            primary={label}
            primaryTypographyProps={{ variant: 'body2', noWrap: true }}
          />
          {/* 꼬리 칸(폭 28px 고정, 가운데 정렬) — 개수가 앉고, ⋮ 메뉴(아래 절대 배치 상자)가
              hover·:focus-within 에 같은 자리에 겹쳐 뜬다. 메뉴 자리를 행마다 따로 예약하지 않아
              이름 가용 폭이 늘고, 칸 폭이 같으니 truncation 기준도 행마다 같다 (#282).
              칸 중심 = 행 오른쪽 끝에서 mx 4 + pr 4 + 14 = 22px; ⋮(30px) 도 right 7 로 중심 22px. */}
          <Typography
            variant="caption"
            color="text.secondary"
            className="ws-row-count"
            sx={{ ml: 1, width: 28, flexShrink: 0, textAlign: 'center' }}
          >
            {count ?? 0}
          </Typography>
        </ListItemButton>
      </Tooltip>
      {(
        // ListItemSecondaryAction 을 쓰지 않는다 — MUI ListItem 이 그 자식을 감지하면 행 전체에
        // paddingRight 48 을 얹어(hasSecondaryAction) 이름 칸이 또 줄어든다. 절대 배치 상자로 겹친다.
        <Box
          sx={{
            position: 'absolute',
            right: 7, // 꼬리 칸 중심(22px)에 ⋮ 중심을 맞춘다 — 행 강조 안쪽(mx 4)에 든다
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconButton
            className="ws-row-menu"
            size="small"
            aria-label={`${label} 메뉴`}
            onClick={(e) => setMenu({ anchor: e.currentTarget, workspace })}
            sx={{ opacity: 0, transition: 'opacity 0.15s' }}
          >
            <MoreIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </ListItem>
    );
  };

  return (
    <Box sx={{ px: 1, pb: 1 }}>
      {/* 섹션 헤더 */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.5, gap: 0.5 }}>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ flexGrow: 1, lineHeight: 2 }}
        >
          Workspaces
        </Typography>
        <Tooltip title={sortMode === 'created' ? '이름순 정렬' : '생성순 정렬'}>
          <IconButton
            size="small"
            aria-label="워크스페이스 정렬"
            onClick={() => setSortMode((m) => (m === 'created' ? 'name' : 'created'))}
          >
            {sortMode === 'created' ? (
              <SortNameIcon fontSize="small" />
            ) : (
              <SortCreatedIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title="워크스페이스 추가">
          <IconButton size="small" aria-label="워크스페이스 추가" onClick={openCreate}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <List dense disablePadding>
        {sorted.map((ws) => renderRow(ws))}
      </List>

      {/* 워크스페이스 행 메뉴 */}
      <Menu
        anchorEl={menu?.anchor ?? null}
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
      >
        <MenuItem onClick={() => menu && openRename(menu.workspace)}>
          <RenameIcon fontSize="small" sx={{ mr: 1 }} /> 이름 변경
        </MenuItem>
        {/* 기본 행은 지울 수 없다(서버도 거절) — 이름은 다른 행과 같이 바꿀 수 있다 */}
        {menu && !menu.workspace.is_default && (
          <MenuItem
            onClick={() => {
              clearError();
              setDeleteTarget(menu.workspace);
              setMenu(null);
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> 삭제
          </MenuItem>
        )}
      </Menu>

      {/* 생성/이름 변경 다이얼로그 */}
      <Dialog open={Boolean(edit)} onClose={() => setEdit(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {edit?.mode === 'create' ? '새 워크스페이스' : '워크스페이스 이름 변경'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            required
            margin="dense"
            label="이름"
            value={edit?.name ?? ''}
            onChange={(e) =>
              setEdit((prev) => prev && { ...prev, name: e.target.value.slice(0, WORKSPACE_NAME_MAX) })
            }
            inputProps={{ maxLength: WORKSPACE_NAME_MAX }}
            helperText={`${edit?.name.length ?? 0}/${WORKSPACE_NAME_MAX}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleEditSubmit();
              }
            }}
          />
          <TextField
            fullWidth
            margin="dense"
            label="설명 (선택)"
            value={edit?.description ?? ''}
            onChange={(e) =>
              setEdit((prev) => prev && { ...prev, description: e.target.value })
            }
          />
          {error && (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdit(null)}>취소</Button>
          <Button
            variant="contained"
            disabled={!edit?.name.trim() || submitting}
            onClick={handleEditSubmit}
          >
            {edit?.mode === 'create' ? '생성' : '저장'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 — 공용 파괴 확인(검출기 ⑤): 진행 중 재진입·닫기 잠금 */}
      <ConfirmDestructiveDialog
        open={Boolean(deleteTarget)}
        title="워크스페이스 삭제"
        message={(
          <>
            "{deleteTarget?.name}" 워크스페이스를 삭제하시겠습니까? 소속 프로젝트
            {counts[deleteTarget?.id ?? ''] ?? 0}개는 삭제되지 않고 "{defaultWorkspace?.name ?? '기본 워크스페이스'}" 로
            이동합니다.
            {error && (
              <Typography component="span" display="block" variant="caption" color="error">
                {error}
              </Typography>
            )}
          </>
        )}
        confirmLabel="삭제"
        pending={submitting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => { void handleDeleteConfirm(); }}
      />
    </Box>
  );
};

export default WorkspaceSection;
